import {
  DB_SCHEMA_VERSION,
  type AlbumDocument,
  type AppSettings,
  type AssetRecord,
  type RecoveryJournalEntry,
} from "../lib/types.ts";

const DB_NAME = "album-library";

export type StoreName =
  | "settings"
  | "albums"
  | "assets"
  | "meta"
  | "journal";

let dbPromise: Promise<IDBDatabase> | null = null;
let blocked = false;

function openDb(): Promise<IDBDatabase> {
  if (blocked) {
    return Promise.reject(new Error("Database blocked by another tab. Reload to continue."));
  }
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_SCHEMA_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains("settings")) {
          db.createObjectStore("settings", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("albums")) {
          db.createObjectStore("albums", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("assets")) {
          db.createObjectStore("assets", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("meta")) {
          db.createObjectStore("meta", { keyPath: "key" });
        }
        if (!db.objectStoreNames.contains("journal")) {
          db.createObjectStore("journal", { keyPath: "key" });
        }
        if (db.objectStoreNames.contains("outbox")) {
          db.deleteObjectStore("outbox");
        }
        if (db.objectStoreNames.contains("syncState")) {
          db.deleteObjectStore("syncState");
        }
      };
      request.onblocked = () => {
        blocked = true;
        reject(new Error("Database upgrade blocked. Close other tabs and reload."));
      };
      request.onerror = () => {
        dbPromise = null;
        reject(request.error ?? new Error("Failed to open IndexedDB"));
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => {
          blocked = true;
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
    });
  }
  return dbPromise;
}

async function withDb<T>(fn: (db: IDBDatabase) => Promise<T>): Promise<T> {
  const db = await openDb();
  return fn(db);
}

function runTx<T>(
  storeNames: StoreName | StoreName[],
  mode: IDBTransactionMode,
  fn: (stores: Record<StoreName, IDBObjectStore>) => IDBRequest<T> | void,
): Promise<T | void> {
  const names = Array.isArray(storeNames) ? storeNames : [storeNames];
  return withDb(
    (db) =>
      new Promise<T | void>((resolve, reject) => {
        const transaction = db.transaction(names, mode);
        const stores = Object.fromEntries(
          names.map((name) => [name, transaction.objectStore(name)]),
        ) as Record<StoreName, IDBObjectStore>;
        const request = fn(stores);
        transaction.oncomplete = () => {
          if (request && "result" in request) resolve(request.result as T);
          else resolve();
        };
        transaction.onerror = () =>
          reject(transaction.error ?? new Error("IndexedDB transaction failed"));
        transaction.onabort = () =>
          reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
      }),
  );
}

export async function getSettings(): Promise<AppSettings | undefined> {
  return (await runTx<AppSettings>("settings", "readonly", (s) => s.settings.get("settings"))) as
    | AppSettings
    | undefined;
}

export async function putSettings(settings: AppSettings): Promise<void> {
  await runTx("settings", "readwrite", (s) => s.settings.put(settings));
}

export async function listAlbums(): Promise<AlbumDocument[]> {
  return withDb(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction("albums", "readonly");
        const request = transaction.objectStore("albums").getAll();
        request.onsuccess = () =>
          resolve(
            (request.result as AlbumDocument[]).sort((a, b) =>
              b.updatedAt.localeCompare(a.updatedAt),
            ),
          );
        request.onerror = () => reject(request.error);
      }),
  );
}

export async function getAlbum(id: string): Promise<AlbumDocument | undefined> {
  return (await runTx<AlbumDocument>("albums", "readonly", (s) => s.albums.get(id))) as
    | AlbumDocument
    | undefined;
}

export async function putAlbum(album: AlbumDocument): Promise<void> {
  await runTx("albums", "readwrite", (s) => s.albums.put(album));
}

export async function deleteAlbum(id: string): Promise<void> {
  await runTx("albums", "readwrite", (s) => s.albums.delete(id));
}

export async function listAssets(): Promise<AssetRecord[]> {
  return withDb(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction("assets", "readonly");
        const request = transaction.objectStore("assets").getAll();
        request.onsuccess = () => resolve(request.result as AssetRecord[]);
        request.onerror = () => reject(request.error);
      }),
  );
}

export async function getAsset(id: string): Promise<AssetRecord | undefined> {
  return (await runTx<AssetRecord>("assets", "readonly", (s) => s.assets.get(id))) as
    | AssetRecord
    | undefined;
}

export async function putAsset(asset: AssetRecord): Promise<void> {
  await runTx("assets", "readwrite", (s) => s.assets.put(asset));
}

export async function deleteAsset(id: string): Promise<void> {
  await runTx("assets", "readwrite", (s) => s.assets.delete(id));
}

export async function putAlbumAndAssets(
  albums: AlbumDocument[],
  assets: AssetRecord[],
): Promise<void> {
  await runTx(["albums", "assets"], "readwrite", (stores) => {
    for (const album of albums) stores.albums.put(album);
    for (const asset of assets) stores.assets.put(asset);
  });
}

export async function listJournal(): Promise<RecoveryJournalEntry[]> {
  return withDb(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction("journal", "readonly");
        const request = transaction.objectStore("journal").getAll();
        request.onsuccess = () => resolve(request.result as RecoveryJournalEntry[]);
        request.onerror = () => reject(request.error);
      }),
  );
}

export async function putJournal(entry: RecoveryJournalEntry): Promise<void> {
  await runTx("journal", "readwrite", (s) => s.journal.put(entry));
}

export async function deleteJournal(key: string): Promise<void> {
  await runTx("journal", "readwrite", (s) => s.journal.delete(key));
}

export async function estimateStorageUsage(): Promise<{ usage: number; quota: number }> {
  if (!navigator.storage?.estimate) return { usage: 0, quota: 0 };
  const estimate = await navigator.storage.estimate();
  return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0 };
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  return navigator.storage.persist();
}

export async function getMeta<T = unknown>(key: string): Promise<T | undefined> {
  const row = (await runTx<{ key: string; value: T }>("meta", "readonly", (s) =>
    s.meta.get(key),
  )) as { key: string; value: T } | undefined;
  return row?.value;
}

export async function putMeta(key: string, value: unknown): Promise<void> {
  await runTx("meta", "readwrite", (s) => s.meta.put({ key, value }));
}

export async function withWriteLock<T>(albumId: string, fn: () => Promise<T>): Promise<T> {
  if ("locks" in navigator && navigator.locks?.request) {
    return navigator.locks.request(`album-write-${albumId}`, fn);
  }
  return fn();
}
